/**
 * PlaybookLinkSection — set the external playbook link (Google Doc, Notion,
 * PDF…) for the current workspace. Lives inside the dashboard theme modal;
 * the hero's book button opens whatever is saved here. Saves on its own
 * (like the background URL row), independent of the theme's Update button.
 */
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import SoftBox from "../SoftBox";
import SoftTypography from "../SoftTypography";
import SoftButton from "../SoftButton";
import { UPDATE_PLAYBOOK_URL } from "../../api/group";

// Accepts "docs.google.com/…" too — a bare domain gets https:// prepended.
export const normalizePlaybookUrl = (value) => {
  const raw = (value || "").trim();
  if (!raw) return { url: "", valid: true, host: "" };
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
  try {
    const parsed = new URL(withScheme);
    const ok = ["http:", "https:"].includes(parsed.protocol) && parsed.hostname.includes(".");
    return { url: ok ? parsed.href : "", valid: ok, host: ok ? parsed.hostname.replace(/^www\./, "") : "" };
  } catch (e) {
    return { url: "", valid: false, host: "" };
  }
};

const PlaybookLinkSection = ({ open, groupId, workspaceName, currentUrl, onSaved }) => {
  const [value, setValue] = useState(currentUrl || "");
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  // Re-seed each time the theme modal opens so an abandoned edit doesn't linger.
  useEffect(() => {
    if (open) {
      setValue(currentUrl || "");
      setTouched(false);
    }
  }, [open, currentUrl]);

  const parsed = useMemo(() => normalizePlaybookUrl(value), [value]);
  const isLinked = !!currentUrl;
  const hasValue = !!value.trim();
  const unchanged = parsed.url === (currentUrl || "");
  const showError = touched && hasValue && !parsed.valid;
  const canSave = !saving && hasValue && parsed.valid && !unchanged;

  let hint = `Players in ${workspaceName || "this workspace"} open this when they tap the book.`;
  if (showError) hint = "That isn't a web link. Try one that starts with https://";
  else if (hasValue && parsed.valid && parsed.host) hint = `Opens ${parsed.host} in a new tab.`;

  const save = async (nextUrl) => {
    if (!groupId) return;
    setSaving(true);
    try {
      const res = await UPDATE_PLAYBOOK_URL(groupId, nextUrl);
      const saved = res?.data?.playbookUrl ?? nextUrl;
      onSaved?.(saved);
      toast.success(saved ? "Playbook linked" : "Playbook link removed");
    } catch (error) {
      const msg =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message;
      toast.error(`Couldn't save the link${msg ? `: ${msg}` : ""}`);
    } finally {
      setSaving(false);
    }
  };

  const submit = () => {
    setTouched(true);
    if (canSave) save(parsed.url);
  };

  return (
    <SoftBox sx={{ marginTop: "1rem" }}>
      <SoftBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
        <SoftTypography sx={{ fontFamily: "DM Sans", fontSize: "13px", fontWeight: 500, color: "#101828" }}>
          Playbook link
        </SoftTypography>
        {isLinked && (
          <SoftBox
            component="button"
            type="button"
            onClick={() => save("")}
            disabled={saving}
            sx={{
              padding: 0,
              border: 0,
              background: "none",
              fontFamily: "DM Sans",
              fontSize: "12px",
              fontWeight: 600,
              color: "#d92d20",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
              "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
            }}
          >
            Remove link
          </SoftBox>
        )}
      </SoftBox>

      <SoftBox sx={{ display: "flex", gap: "8px", marginTop: "8px" }}>
        <SoftBox
          component="input"
          type="url"
          inputMode="url"
          autoComplete="off"
          spellCheck={false}
          placeholder="https://docs.google.com/…"
          value={value}
          disabled={saving}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => setTouched(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          aria-label="Playbook URL"
          aria-invalid={showError || undefined}
          sx={{
            flex: 1,
            minWidth: 0,
            height: "40px",
            padding: "0 12px",
            background: "#FFFFFF",
            border: `1px solid ${showError ? "#d92d20" : "#D0D5DD"}`,
            borderRadius: "8px",
            color: "#191D23",
            fontSize: "13px",
            outline: "none",
            "&::placeholder": { color: "#98A2B3" },
          }}
        />
        <SoftButton
          onClick={submit}
          disabled={!canSave}
          sx={{
            background: "#FFFFFF",
            border: "1px solid #D0D5DD",
            borderRadius: "8px",
            color: "#344054",
            textTransform: "none",
            minWidth: "84px",
            boxShadow: "none",
            "&:hover": { background: "#F9FAFB", boxShadow: "none" },
          }}
        >
          {saving ? "Saving…" : "Save"}
        </SoftButton>
      </SoftBox>
      <SoftTypography sx={{ fontSize: "12px", color: showError ? "#d92d20" : "#98A2B3", marginTop: "6px" }}>
        {hint}
      </SoftTypography>
    </SoftBox>
  );
};

export default PlaybookLinkSection;
